import React, { useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../apis/apiFetch";

import { useRaspStore } from "../../store/raspStore";
import { useEducation10thDetailsViewModel } from "../../viewModels/useEducation10thDetailsViewModel";
import { LoginContext } from '../../context/LoginContext';
import './FormStyles.css';
import { validate } from "../../utils/validator";
import { education10thValidationRules } from "../../validators/applicantValidator";
import { BTechApplicationFields } from "../../config/btechApplicationFields";

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface Education10thDetailsFormRef {
  save: () => Promise<void>;
  getErrors: () => Record<string, string>;
}

export type CreateEducation10thDetailsProps = {
  /** `application`: fields stored on `applications` rows (B.Tech). `resource`: legacy standalone-style form. */
  variant?: "resource" | "application";
  applicationDraft?: Record<string, any>;
  onApplicationDraftChange?: (patch: Record<string, any>) => void;
  applicationFieldErrors?: Record<string, string>;
};

const K = BTechApplicationFields.edu10;

function Education10thApplicationFields({
  draft,
  onPatch,
  fieldErrors = {},
}: {
  draft: Record<string, any>;
  onPatch: (patch: Record<string, any>) => void;
  fieldErrors?: Record<string, string>;
}) {
  const [enums, setEnums] = useState<any>({});

  useEffect(() => {
    fetchEnum("Marksformat").then((vals) => setEnums({ Marksformat: vals }));
  }, []);

  const v = (field: string) => draft[field] ?? "";
  const set = (field: string, value: string | File | null) => {
    onPatch({ [field]: value });
  };

  const marksObtained = Number(v(K.marksobtained)) || 0;
  const maximumMarks = Number(v(K.maximummarks)) || 0;

  let rawPercentage =
    maximumMarks > 0 ? (marksObtained / maximumMarks) * 100 : 0;

  const percentage =
    rawPercentage > 100 || marksObtained < 0 || maximumMarks < 0
      ? 0
      : rawPercentage;

  const isGradeSelected = v(K.marksformat) && v(K.marksformat).toLowerCase() !== "percentage";
  const [isPercentageTouched, setIsPercentageTouched] = useState(false);
  const isPercentageValid =
    isGradeSelected ?
      maximumMarks === 0 || (percentage >= 60 && percentage <= 100)
      : !isNaN(parseFloat(String(v(K.percentage)).trim())) && parseFloat(String(v(K.percentage)).trim()) >= 60;

  useEffect(() => {
    if (maximumMarks > 0) {
      if (isGradeSelected) {
        onPatch({
          edu10_percentage: parseFloat(percentage.toFixed(2)),
        });
      }
    }
  }, [marksObtained, maximumMarks]);

  useEffect(() => {
    if (!isGradeSelected) {
      onPatch({
        edu10_marksobtained: null,
        edu10_maximummarks: null,
        edu10_grade: null,
      });
    }
  }, [isGradeSelected])


  return (
    <div className="form-container">
      <h3 className="form-subtitle">Class Xth / SSC</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Education Board *</label>
          <input
            type="text"
            className={`form-input ${fieldErrors[K.board] ? "input-error" : ""}`}
            value={v(K.board)}
            placeholder="Education Board"
            onChange={(e) => set(K.board, e.target.value)}
          />
          {fieldErrors[K.board] && <span className="error-text">{fieldErrors[K.board]}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">School/College *</label>
          <input
            type="text"
            className={`form-input ${fieldErrors[K.schoolname] ? "input-error" : ""}`}
            value={v(K.schoolname)}
            placeholder="School/College Name"
            onChange={(e) => set(K.schoolname, e.target.value)}
          />
          {fieldErrors[K.schoolname] && <span className="error-text">{fieldErrors[K.schoolname]}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">Grade/Marks Type *</label>
          <select
            className={`form-select ${fieldErrors[K.marksformat] ? "input-error" : ""}`}
            value={v(K.marksformat)}
            onChange={(e) => set(K.marksformat, e.target.value)}
          >
            <option value="">Select</option>
            {Object.keys(enums).length !== 0 && enums["Marksformat"] !== undefined &&
              enums["Marksformat"]?.map((val: any, idx: any) => (
                <option key={idx} value={val}>{val}</option>
              ))}
          </select>
          {fieldErrors[K.marksformat] && <span className="error-text">{fieldErrors[K.marksformat]}</span>}
        </div>
        {isGradeSelected && <div className="form-group">
          <label className="form-label">Marks / CGPA Obtained *</label>
          <input
            type="text"
            className={`form-input ${fieldErrors[K.marksobtained] ? "input-error" : ""}`}
            value={v(K.marksobtained)}
            placeholder="Marks Obtained"
            onChange={(e) => set(K.marksobtained, e.target.value)}
          />
          {fieldErrors[K.marksobtained] && <span className="error-text">{fieldErrors[K.marksobtained]}</span>}
        </div>}
        {isGradeSelected && <div className="form-group">
          <label className="form-label">Maximum Marks / CGPA *</label>
          <input
            type="text"
            className={`form-input ${fieldErrors[K.maximummarks] ? "input-error" : ""}`}
            value={v(K.maximummarks)}
            placeholder="Maximum Marks"
            onChange={(e) => set(K.maximummarks, e.target.value)}
          />
          {fieldErrors[K.maximummarks] && <span className="error-text">{fieldErrors[K.maximummarks]}</span>}
        </div>}
        <div className="form-group">
          <label className="form-label">Percentage</label>
          <input
            type={isGradeSelected ? "text" : "number"}
            className="form-input"
            readOnly={isGradeSelected}
            value={isGradeSelected ? (maximumMarks > 0 ? `${percentage.toFixed(2)}%` : "—") : v(K.percentage)}
            style={isGradeSelected ? {
              backgroundColor: "#e9ecef",
              cursor: "not-allowed",
              color:
                !isPercentageValid && isPercentageTouched
                  ? "#dc3545"
                  : "#28a745",
              fontWeight: "bold",
            } : undefined}
            onChange={
              !isGradeSelected
                ? (e) => {
                  setIsPercentageTouched(true);
                  const value = e.target.value;
                  if (value === "") {
                    set(K.percentage, "");
                    return;
                  }
                  const num = Number(value);
                  if (!isNaN(num) && num >= 0 && num <= 100) {
                    set(K.percentage, value);
                  }
                }
                : undefined
            }
          />

          {!isPercentageValid && isPercentageTouched && (
            <small
              style={{
                color: "#dc3545",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {isGradeSelected
                ? marksObtained < 0 ||
                  maximumMarks < 0 ||
                  (marksObtained / maximumMarks) * 100 > 100
                  ? "Invalid marks."
                  : "Minimum 60% is required to apply for B.Tech/IM.Tech programme."
                : isNaN(parseFloat(String(v(K.percentage)).trim())) ? "Invalid Percentage" : "Minimum 60% is required to apply for B.Tech/IM.Tech programme."}            </small>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Year of Passing *</label>
          <select
            className={`form-select ${fieldErrors[K.passingyear] ? "input-error" : ""}`}
            value={v(K.passingyear)}
            onChange={(e) => set(K.passingyear, e.target.value)}
          >
            <option value="">Select Year</option>
            {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          {fieldErrors[K.passingyear] && <span className="error-text">{fieldErrors[K.passingyear]}</span>}
        </div>
      </div>
    </div>
  );
}

const CreateEducation10thDetails = forwardRef<Education10thDetailsFormRef, CreateEducation10thDetailsProps>((props, ref) => {
  const { variant = "resource", applicationDraft, onApplicationDraftChange, applicationFieldErrors } = props;

  if (variant === "application") {
    if (!applicationDraft || !onApplicationDraftChange) {
      return null;
    }
    return (
      <Education10thApplicationFields
        draft={applicationDraft}
        onPatch={onApplicationDraftChange}
        fieldErrors={applicationFieldErrors}
      />
    );
  }

  return <CreateEducation10thResourceInner ref={ref} />;
});

const CreateEducation10thResourceInner = forwardRef<Education10thDetailsFormRef>((props, ref) => {
  const { user } = useContext(LoginContext);

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useEducation10thDetailsViewModel(user?.userId || "", appId || "");

  useEffect(() => {
    loadMetadata();
  }, []);

  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find(
      (u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "").toLowerCase().trim();
        return userEmail === user.email_id.toLowerCase().trim();
      }
    );

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
        level: "10",
        specialization: "General"
      }));
    } else {
      setDataToSave((prev: any) => ({
        ...prev,
        level: "10",
        specialization: "General"
      }));
    }
  }, [user, foreignKeyData]);

  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  useQuery({
    queryKey: ["resMetaData", "Education10thDetailsCreate"],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter(
        (field: any) => field.foreign
      );
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ["foreignData", field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(
            field.foreign,
            field.name,
            field.foreign_field
          );
        }
      }

      const enumFields = data[0].fieldValues.filter(
        (field: any) => field.isEnum === true
      );
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ["enum", field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  const { data: dataRes } = useQuery({
    queryKey: ["resourceData", "Education10thDetailsCreate", user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  });

  useEffect(() => {
    if (dataRes) {
      let dataToStore = { resource: "Education10thDetails", records: dataRes };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user]);

  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!hasPrePopulated.current && dataRes && dataRes.length > 0 && dataToSave.user_id && activeDriveId) {
      const userRecord = dataRes.find((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
      if (userRecord) {
        hasPrePopulated.current = true;
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
      }
    }
  }, [dataRes, dataToSave.user_id])

  const handleCreate = async () => {
    const vErrors = validate(dataToSave, education10thValidationRules);
    setErrors(vErrors);

    await save();
    queryClient.invalidateQueries({ queryKey: ['resourceData', 'Education10thDetailsCreate'] });
  };

  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors
  }));

  const marksObtained = Number(dataToSave["marksobtained"]) || 0;
  const maximumMarks = Number(dataToSave["maximummarks"]) || 0;

  let rawPercentage =
    maximumMarks > 0 ? (marksObtained / maximumMarks) * 100 : 0;

  const percentage =
    rawPercentage > 100 || marksObtained < 0 || maximumMarks < 0
      ? 0
      : rawPercentage;

  const isPercentageValid =
    maximumMarks === 0 || (percentage >= 60 && percentage <= 100);

  useEffect(() => {
    if (maximumMarks > 0) {
      setDataToSave((prev: any) => ({
        ...prev,
        edu10_percentage: parseFloat(percentage.toFixed(2)),
      }));
    }
  }, [marksObtained, maximumMarks]);

  return (
    <div className="form-container">
      <h3 className="form-subtitle">10th or Equivalent</h3>

      {(() => {
        const isGradeSelected = dataToSave["marksformat"] && dataToSave["marksformat"].toLowerCase() !== "percentage";
        return (
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Education Board *</label>
              <input
                type="text"
                className={`form-input ${errors.board ? "input-error" : ""}`}
                name="board"
                required
                value={dataToSave["board"] || ""}
                placeholder="Education Board"
                onChange={(e) => {
                  setDataToSave({ ...dataToSave, board: e.target.value });
                  if (errors.board) setErrors(prev => ({ ...prev, board: "" }));
                }}
              />
              {errors.board && <span className="error-text">{errors.board}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">School/College *</label>
              <input
                type="text"
                className={`form-input ${errors.schoolname ? "input-error" : ""}`}
                name="schoolname"
                required
                value={dataToSave["schoolname"] || ""}
                placeholder="School/College Name"
                onChange={(e) => {
                  setDataToSave({ ...dataToSave, schoolname: e.target.value });
                  if (errors.schoolname) setErrors(prev => ({ ...prev, schoolname: "" }));
                }}
              />
              {errors.schoolname && <span className="error-text">{errors.schoolname}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Grade/Marks Type *</label>
              <select
                className={`form-select ${errors.marksformat ? "input-error" : ""}`}
                name="marksformat"
                required
                value={dataToSave["marksformat"] || ""}
                onChange={(e) => {
                  setDataToSave({ ...dataToSave, marksformat: e.target.value });
                  if (errors.marksformat) setErrors(prev => ({ ...prev, marksformat: "" }));
                }}
              >
                <option value="">Select</option>
                {Object.keys(enums).length !== 0 && enums["Marksformat"] !== undefined &&
                  enums["Marksformat"]?.map((val: any, idx: any) => (
                    <option key={idx} value={val}>{val}</option>
                  ))}
              </select>
              {errors.marksformat && <span className="error-text">{errors.marksformat}</span>}
            </div>

            {isGradeSelected && (
              <div className="form-group">
                <label className="form-label">Marks Obtained *</label>
                <input
                  type="text"
                  className={`form-input ${errors.marksobtained ? "input-error" : ""}`}
                  name="marksobtained"
                  required
                  value={dataToSave["marksobtained"] || ""}
                  placeholder="Marks Obtained"
                  onChange={(e) => {
                    setDataToSave({ ...dataToSave, marksobtained: e.target.value });
                    if (errors.marksobtained) setErrors(prev => ({ ...prev, marksobtained: "" }));
                  }}
                />
                {errors.marksobtained && <span className="error-text">{errors.marksobtained}</span>}
              </div>
            )}

            {isGradeSelected && (
              <div className="form-group">
                <label className="form-label">Maximum Marks *</label>
                <input
                  type="text"
                  className={`form-input ${errors.maximummarks ? "input-error" : ""}`}
                  name="maximummarks"
                  required
                  value={dataToSave["maximummarks"] || ""}
                  placeholder="Maximum Marks"
                  onChange={(e) => {
                    setDataToSave({ ...dataToSave, maximummarks: e.target.value });
                    if (errors.maximummarks) setErrors(prev => ({ ...prev, maximummarks: "" }));
                  }}
                />
                {errors.maximummarks && <span className="error-text">{errors.maximummarks}</span>}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Percentage</label>
              <input
                type={isGradeSelected ? "text" : "number"}
                className="form-input"
                readOnly={isGradeSelected}
                value={isGradeSelected ? (maximumMarks > 0 ? `${percentage.toFixed(2)}%` : "—") : dataToSave["edu10_percentage"]}
                style={{
                  backgroundColor: isGradeSelected ? "#e9ecef" : undefined,
                  cursor: isGradeSelected ? "not-allowed" : undefined,
                  color:
                    !isPercentageValid && maximumMarks > 0
                      ? "#dc3545"
                      : "#28a745",
                  fontWeight: "bold",
                }}
                onChange={!isGradeSelected ? (e) => {
                  setDataToSave({ ...dataToSave, edu10_percentage: e.target.value });
                } : undefined}
              />

              {!isPercentageValid && maximumMarks > 0 && (
                <small
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {marksObtained < 0 ||
                    maximumMarks < 0 ||
                    (marksObtained / maximumMarks) * 100 > 100
                    ? "Invalid marks."
                    : "Minimum 60% is required to apply for B.Tech/IM.Tech programme."}
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Year of Passing *</label>
              <select
                className={`form-select ${errors.passingyear ? "input-error" : ""}`}
                name="passingyear"
                required
                value={dataToSave["passingyear"] || ""}
                onChange={(e) => {
                  setDataToSave({ ...dataToSave, passingyear: e.target.value });
                  if (errors.passingyear) setErrors(prev => ({ ...prev, passingyear: "" }));
                }}
              >
                <option value="">Select Year</option>
                {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.passingyear && <span className="error-text">{errors.passingyear}</span>}
            </div>

            {dataToSave["marksformat"]?.toLowerCase() === "grade" && (
              <div className="form-group">
                <label className="form-label">Grade *</label>
                <input
                  type="text"
                  className={`form-input ${errors.grade ? "input-error" : ""}`}
                  name="grade"
                  required
                  value={dataToSave["grade"] || ""}
                  placeholder="Grade"
                  onChange={(e) => {
                    setDataToSave({ ...dataToSave, grade: e.target.value });
                    if (errors.grade) setErrors(prev => ({ ...prev, grade: "" }));
                  }}
                />
                {errors.grade && <span className="error-text">{errors.grade}</span>}
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
});

CreateEducation10thResourceInner.displayName = "CreateEducation10thResourceInner";
CreateEducation10thDetails.displayName = "CreateEducation10thDetails";

export default CreateEducation10thDetails;
